import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { chartColors, formatCurrency } from "./chartUtils";

interface LineChartData {
  name: string;
  [key: string]: string | number;
}

interface LineChartProps {
  data: LineChartData[];
  dataKeys: { key: string; color?: string; name?: string; dashed?: boolean }[];
  title?: string;
  showLegend?: boolean;
  showGrid?: boolean;
  height?: number;
  showDots?: boolean;
}

export function LineChart({
  data,
  dataKeys,
  title,
  showLegend = true,
  showGrid = true,
  height = 300,
  showDots = true,
}: LineChartProps) {
  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: { value: number; name: string; color: string }[];
    label?: string;
  }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-stone/10">
          <p className="font-medium text-charcoal mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full" style={{ height }}>
      {title && (
        <h3 className="text-sm font-medium text-charcoal mb-2">{title}</h3>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <RechartsLineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#E8E3DC" />}
          <XAxis
            dataKey="name"
            tick={{ fontSize: 12, fill: "#8B8589" }}
            axisLine={{ stroke: "#E8E3DC" }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 12, fill: "#8B8589" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value) => formatCurrency(value)}
          />
          <Tooltip content={<CustomTooltip />} />
          {showLegend && (
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value: string) => (
                <span className="text-sm text-charcoal">{value}</span>
              )}
            />
          )}
          {dataKeys.map((dk, index) => (
            <Line
              key={dk.key}
              type="monotone"
              dataKey={dk.key}
              name={dk.name || dk.key}
              stroke={dk.color || chartColors.primary[index % chartColors.primary.length]}
              strokeWidth={2}
              strokeDasharray={dk.dashed ? "5 5" : undefined}
              dot={showDots ? { r: 4, strokeWidth: 2 } : false}
              activeDot={{ r: 6, strokeWidth: 2 }}
            />
          ))}
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
}
